/**
 * @fileoverview Key Date Derivations
 *
 * Purpose:
 *  - Pure functions that turn a `jobs` row into the calendar events the
 *    practitioner would expect to see for a fresh appointment.
 *  - Two layers:
 *      1. **Anchors** — appointment, commencement, relation-back, court
 *         order, act of bankruptcy. These are the dates that drive every
 *         downstream calculation, so we expose them as their own events.
 *      2. **Statutory lodgements** — Form 505 / DIRRI / ROCAP / 5601 etc,
 *         keyed off the appointment type. This is a deliberately small
 *         starter set; the full lodgement engine (BAS/STP, AAR variations,
 *         meeting minutes, RAEA, AAF, dividend notices) is intentionally
 *         out of scope for v1.
 *
 *  - Output rows match the shape returned by `convex/keyDates/queries.ts`
 *    so the listing query can merge derived + persisted entries without
 *    bespoke adapters per source.
 *
 * Dependencies:
 *  - No `ctx` access — pure functions are easy to unit test and easy to
 *    reuse from `derivations.test.ts` once we add coverage.
 *
 * Used by:
 *  - convex/keyDates/queries.ts (listForJob, listForOrg)
 */

import type { Doc } from "@cvx/_generated/dataModel";
import type {
  KeyDateCategory,
  KeyDateFlag,
  KeyDateSource,
  KeyDateStatus,
} from "@cvx/keyDates/jobKeyDates/schema";
import type { AppointmentType } from "@cvx/jobs/schema";
import {
  deriveBankruptcyCommencement,
  bankruptcyInputsFromJob,
  type BankruptcyCommencementResult,
} from "./bankruptcyCommencement";
import {
  isBankruptcyAppointmentType,
  isPersonalInsolvencyAppointmentType,
} from "@cvx/lib/appointmentTypes";
// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * Server shape of a derived event. Mirrors the column subset on
 * `jobKeyDates` that the calendar UI consumes, plus a derived `isDerived`
 * flag so the client can disable destructive actions on entries that
 * don't exist as rows yet.
 */
export interface DerivedKeyDate {
  /** Stable id so React keys are stable across re-renders. */
  id: string;
  jobId: Doc<"jobs">["_id"];
  orgId: Doc<"organizations">["_id"];
  source: KeyDateSource;
  category: KeyDateCategory;
  /** Stable key (`anchor_…`, `form_…`) so overrides can shadow this entry. */
  code: string;
  title: string;
  description?: string;
  /** ISO YYYY-MM-DD. */
  date: string;
  endDate?: string;
  allDay: boolean;
  startTime?: string;
  endTime?: string;
  flag: KeyDateFlag;
  status: KeyDateStatus;
  notes?: string;
  isDerived: true;
}

// ---------------------------------------------------------------------------
// Date arithmetic helpers (pure, no date-fns dep in convex layer)
// ---------------------------------------------------------------------------

/** Parse `YYYY-MM-DD` into a UTC midnight Date. Returns null on bad input. */
export function parseIsoDate(iso: string | undefined): Date | null {
  if (!iso) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return null;
  }
  return new Date(Date.UTC(year, month - 1, day));
}

/** Format a UTC Date as `YYYY-MM-DD`. */
export function formatIsoDate(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Add `n` calendar days to a UTC date. */
export function addCalendarDays(date: Date, n: number): Date {
  const out = new Date(date.getTime());
  out.setUTCDate(out.getUTCDate() + n);
  return out;
}

/**
 * Add `n` Australian business days (Mon–Fri) — public holidays are
 * intentionally ignored in v1 (no AU holiday calendar in the repo yet).
 * Negative `n` walks backwards.
 */
function addBusinessDays(date: Date, n: number): Date {
  if (n === 0) return new Date(date.getTime());
  const step = n > 0 ? 1 : -1;
  let remaining = Math.abs(n);
  let cursor = new Date(date.getTime());
  while (remaining > 0) {
    cursor = addCalendarDays(cursor, step);
    const dow = cursor.getUTCDay();
    if (dow !== 0 && dow !== 6) {
      remaining -= 1;
    }
  }
  return cursor;
}

/**
 * Add `n` calendar months. Anchors to end-of-month when the day-of-month
 * doesn't exist in the target month (e.g. 31 Jan + 1 month = 28/29 Feb).
 */
export function addCalendarMonths(date: Date, n: number): Date {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  const targetMonth = month + n;
  const candidate = new Date(Date.UTC(year, targetMonth, day));
  if (candidate.getUTCMonth() !== ((targetMonth % 12) + 12) % 12) {
    // Day overflowed (e.g. 31 → 30); clamp to last day of intended month.
    return new Date(Date.UTC(year, targetMonth + 1, 0));
  }
  return candidate;
}

function isCorporateAppointment(type: AppointmentType | undefined): boolean {
  if (!type) return false;
  return !isPersonalInsolvencyAppointmentType(type);
}

/**
 * True when the individual is a bankrupt (MOR-1838: includes the Creditors
 * and Debtors Petition types; a Part X arrangement is personal insolvency
 * but not a bankruptcy, so none of the Bankruptcy Act estate rules apply).
 */
export function isBankruptcyAppointment(
  type: AppointmentType | undefined,
): boolean {
  return isBankruptcyAppointmentType(type);
}

/**
 * Bankruptcy jobs: the relation-back analogue is the COMMENCEMENT of
 * bankruptcy (s 115), computed from the case record — not the act of
 * bankruptcy date, which is one input to it (MOR-1253).
 */
function bankruptcyRelationBackDefault(
  commencement: BankruptcyCommencementResult,
): RelationBackDefault {
  return {
    date: commencement.commencement,
    applies: true,
    pending: commencement.commencement === null,
    assumed: false,
    reason: commencement.note,
    title: "Commencement of bankruptcy (s 115)",
    description:
      "Commencement of bankruptcy — the date the bankruptcy relates back to under s 115. Anchors the s 120 undervalued transfer window; the s 122 preference window runs from presentation of the petition except where a creditor's petition was pending.",
  };
}

// ---------------------------------------------------------------------------
// Relation-back day (RBD) — single source of truth (MOR-542)
// ---------------------------------------------------------------------------

/**
 * Resolved relation-back day for a job, computed per appointment type.
 *
 * - `date` is the ISO RBD, or `null` when no RBD applies (or its source
 *   date hasn't been entered yet).
 * - `applies` is `false` for appointment types that can never have an RBD
 *   (receivership, scheme of arrangement — not windings up).
 * - `pending` is `true` when an RBD would apply in principle but isn't
 *   available yet: the required source date (appointment / winding-up
 *   application / act of bankruptcy) hasn't been captured.
 * - `assumed` is `true` when the type has no *statutory* relation-back day
 *   (VA / DOCA / SBR / provisional liquidation are not windings up), but we
 *   treat the date of appointment as an assumed relation-back day so the
 *   voidable-transaction analysis can run — practitioners must present that
 *   liquidation scenario in their report to creditors (MOR-636). The UI must
 *   disclose this is an assumption, not a real RBD.
 * - `reason` is practitioner-facing copy explaining a `null` `date`, or the
 *   assumption when `assumed` is `true`.
 */
export interface RelationBackResult {
  date: string | null;
  applies: boolean;
  pending: boolean;
  assumed: boolean;
  reason: string | null;
  title: string;
  description: string;
  /**
   * Which date the day was taken from, or `null` when it fell through to a
   * per-type default (MOR-901).
   */
  basis: RelationBackBasis | null;
  /** Statutory reference for display, e.g. "ss 513B(b), 513C & 91". */
  section: string | null;
  /** True when a practitioner override supplied the date. */
  isOverride: boolean;
}

/**
 * Which date the relation-back day was taken from (MOR-901).
 *
 * The ILRA s 91 table has many rows, but for the pathways Morgan models they
 * all resolve to one of these two anchors.
 */
export type RelationBackBasis =
  | "application_filed"
  | "administrators_appointed"
  /** Commencement of bankruptcy computed under s 115 (MOR-1253). */
  | "bankruptcy_commencement";

/**
 * Everything except the basis/override metadata, which
 * {@link deriveRelationBack} layers on top of the per-type default.
 */
type RelationBackDefault = Omit<
  RelationBackResult,
  "basis" | "section" | "isOverride"
>;

/**
 * Appointment types where a winding-up application filed before the
 * administration can govern the relation-back day (MOR-901).
 *
 * The QUESTION is only asked on a voluntary administration — that is the only
 * appointment that can be preceded by a live winding-up application. But the
 * ANSWER has to keep being read after the VA converts: `appointmentSwitchPatch`
 * changes `appointmentType` to `cvl` / `court_liquidation` and carries the
 * administration date into `administrationStartDate`, so if the liquidation
 * types were excluded here the relation-back day would silently jump forward at
 * the exact moment it stops being a scenario and becomes the statutory day.
 *
 * An MVL is a members' winding up, an SBR is Part 5.3B, and receivership /
 * scheme / bankruptcy are not windings up at all — none can follow a winding-up
 * application, so all keep their per-type defaults. Provisional liquidation is
 * also excluded: its default already prefers the winding-up application date.
 */
const PRIOR_APPLICATION_TYPES: ReadonlySet<string> = new Set([
  "cvl",
  "simplified_liquidation",
  "court_liquidation",
  "voluntary_administration",
  "deed_of_company_arrangement",
]);

/**
 * Appointment types that are not (yet) a winding up, so any relation-back day
 * is a liquidation-scenario assumption rather than a statutory day (MOR-636).
 */
const ASSUMED_RBD_TYPES: ReadonlySet<string> = new Set([
  "voluntary_administration",
  "deed_of_company_arrangement",
  "sbr",
  "provisional_liquidation",
]);

/**
 * Resolve the relation-back day from a winding-up application that preceded
 * the administration (MOR-901).
 *
 * The ILRA s 91 table runs to sixteen pathways once you enumerate the deed
 * permutations (VA → CVL, CAF → VA → OL, VA → DOCA → creditors terminate →
 * CVL, and so on), but every one of them turns on a single question:
 *
 *   > Was a winding-up application filed BEFORE the administration began, and
 *   > never dismissed or withdrawn?
 *
 * Yes → the day is the date that application was FILED (s 513B(b) via s 91).
 * No  → the day is the date the administrators were appointed, the s 513C day.
 *
 * The deed stages don't change the answer: a deed that fails traces back to the
 * original administration, which is the same s 513C day the pre-deed pathway
 * would have produced. `relationBackPriorApplication.test.ts` walks all sixteen
 * rows of the table against this predicate to prove that.
 *
 * Returns `null` when the question can't produce a date, leaving the caller to
 * apply the per-type default.
 */
function resolvePriorApplication(
  job: Doc<"jobs">,
  dates: {
    appointment: string | null;
    applicationFiled: string | null;
    administrationStart: string | null;
  },
): RelationBackResult | null {
  const type = job.appointmentType;
  if (!type || !PRIOR_APPLICATION_TYPES.has(type)) return null;

  // Dismissed or withdrawn: the application can no longer anchor the day, so
  // fall through to the per-type default (the s 513C day on an administration
  // route). Checked before the dates so an explicit answer always wins over
  // what the dates alone would imply.
  const status = job.windingUpApplicationStatus;
  if (status === "dismissed" || status === "withdrawn") return null;

  // The day the administrators were appointed. On a job whose CURRENT
  // appointment is the administration, that is the appointment date itself.
  // A DOCA job is deliberately excluded from the fallback: its appointment
  // date may hold the deed date, and a failed deed must trace to the
  // ORIGINAL administration (same reasoning as `appointmentSwitchPatch`).
  const administratorsAppointed =
    dates.administrationStart ??
    (type === "voluntary_administration" ? dates.appointment : null);

  const filed = dates.applicationFiled;
  if (!filed) return null;

  // With no administration in the picture there is nothing for the filing date
  // to precede — a court liquidation's own application is handled by the
  // per-type default, which already returns the filing date.
  if (!administratorsAppointed) return null;

  // Filed on or after the administration began, so it is not a PRIOR
  // application: the s 513C day governs via the default.
  if (filed >= administratorsAppointed) return null;

  const assumed = ASSUMED_RBD_TYPES.has(type);

  // A VA/DOCA is not a winding up, so the day is applied rather than
  // statutory for now. It is the day s91 will fix if the company moves into
  // liquidation, so investigations run against the right date; only a
  // liquidator can act on what they find.
  const reason = assumed
    ? `Applied relation-back date is the date the winding-up application was filed (${filed}). It was filed before the administration began on ${administratorsAppointed} and has not been dismissed or withdrawn, so it is the relation-back day under s91 if the company goes into liquidation. Only a liquidator has the power to recover transactions.`
    : null;

  return {
    date: filed,
    applies: true,
    pending: false,
    assumed,
    reason,
    // Titles follow the existing convention (unspaced "s91"); `section`
    // carries the fuller citation for display alongside the date.
    title: assumed
      ? "Applied relation-back date (s91)"
      : "Relation-back date (s91)",
    description:
      "Date the winding-up application was filed — it was filed before the administration began and has not been dismissed or withdrawn, so it anchors the relation-back day (s91).",
    basis: "application_filed",
    section: "ss 513B(b) & 91",
    isOverride: false,
  };
}

/**
 * The relation-back day for a job, resolved in three tiers (MOR-901):
 *
 *   1. `relationBackDateOverride` — a practitioner override wins outright,
 *      except on a Part X arrangement, where no relation-back day can apply
 *      (MOR-1838) so an override inherited from an earlier appointment type
 *      is ignored.
 *   2. A winding-up application that preceded the administration and is still
 *      on foot — see {@link resolvePriorApplication}.
 *   3. The per-appointment-type default below, unchanged.
 *
 * Tier 2 is what corrects historic jobs. The previous implementation always
 * preferred `administrationStartDate` on an administration-route job, which is
 * right when nothing preceded the administration but wrong when a winding-up
 * application did — the filing date governs and is EARLIER, so every look-back
 * window was being cut short.
 *
 * @example
 * // Application filed 2026-01-20, administrators appointed 2026-03-28,
 * // application still on foot: the filing date governs.
 * deriveRelationBack(job).date; // "2026-01-20"
 */
export function deriveRelationBack(job: Doc<"jobs">): RelationBackResult {
  const iso = (raw: string | undefined): string | null => {
    const parsed = parseIsoDate(raw);
    return parsed ? formatIsoDate(parsed) : null;
  };

  // A Part X arrangement (MOR-1838) is neither a bankruptcy nor a winding up,
  // so no relation-back day applies — not even a manual override, which can
  // only be left over from a previous appointment type. Checked first so the
  // calendar anchor and the windows never publish one.
  if (
    isPersonalInsolvencyAppointmentType(job.appointmentType) &&
    !isBankruptcyAppointment(job.appointmentType)
  ) {
    return {
      ...deriveRelationBackDefault(job),
      basis: null,
      section: null,
      isOverride: false,
    };
  }

  // Tier 1 — practitioner override. Honoured here rather than in the UI so
  // the calendar anchor, the voidable-transaction windows and the AI agents
  // all read the same date.
  const override = iso(job.relationBackDateOverride);
  if (override) {
    return {
      date: override,
      applies: true,
      pending: false,
      assumed: false,
      reason: `Relation-back day set manually to ${override}, overriding the derived date.`,
      title: "Relation-back date (manual override)",
      description:
        "Set manually in job settings. Clear the override to return to the derived date.",
      basis: null,
      section: null,
      isOverride: true,
    };
  }

  // Tier 1b — bankruptcy: commencement under s 115 (MOR-1253). Sits above
  // the corporate tiers because none of them apply to a bankruptcy.
  if (isBankruptcyAppointment(job.appointmentType)) {
    const commencement = deriveBankruptcyCommencement(
      bankruptcyInputsFromJob(job),
    );
    return {
      ...bankruptcyRelationBackDefault(commencement),
      basis: "bankruptcy_commencement",
      section: commencement.section,
      isOverride: false,
    };
  }

  // Tier 2 — a winding-up application that preceded the administration.
  const resolved = resolvePriorApplication(job, {
    appointment: iso(job.appointmentDate),
    applicationFiled: iso(job.windingUpApplicationDate),
    administrationStart: iso(job.administrationStartDate),
  });
  if (resolved) return resolved;

  // Tier 3 — per-appointment-type default.
  return {
    ...deriveRelationBackDefault(job),
    basis: null,
    section: null,
    isOverride: false,
  };
}

/**
 * The relation-back day (s9 → the s91 table, Corporations Act; s115 Bankruptcy
 * Act) anchors every voidable-transaction lookback window. It varies by
 * appointment type:
 *
 *   CVL / MVL / Simplified Liq → date of appointment (winding-up resolution);
 *                                 where a voluntary administration preceded the
 *                                 liquidation (the s446A route), the s513C day —
 *                                 the day the administration began
 *                                 (`administrationStartDate`, MOR-813).
 *   Court Liquidation          → date the winding-up application was *filed*
 *                                 (`windingUpApplicationDate`, earlier than
 *                                 appointment; manual entry today); where the
 *                                 order was made while the company was under
 *                                 administration or a since-failed DOCA, the
 *                                 s513C day of the ORIGINAL administration
 *                                 (`administrationStartDate`, MOR-813).
 *   Provisional Liq            → assumed RBD (MOR-636): winding-up application
 *                                 date if filed, else provisional appointment
 *                                 date — flagged `assumed` for UI disclosure.
 *   VA / DOCA / SBR            → assumed RBD (MOR-636): date of appointment, so
 *                                 the voidable-transaction scenario can be
 *                                 analysed for the report to creditors. Flagged
 *                                 `assumed`; on conversion to liquidation the
 *                                 real type-specific RBD supersedes it.
 *   Receivership / Scheme      → none ever — not a winding up.
 *   Bankruptcy                 → commencement of bankruptcy computed under
 *                                 s 115 from the case record (petition type,
 *                                 presentation date, act relied on) — see
 *                                 `bankruptcyCommencement.ts` (MOR-1253).
 *
 * Citation note (MOR-813): since the 2016 ILRA the relation-back day is the
 * s91 table (s9 definition points to it); ss513A–513C define when a winding up
 * is "taken to have begun" (commencement), and the s91 table routes the
 * administration pathways to "the section 513C day". Practitioner-facing copy
 * therefore cites s91 and names the s513C day for administration routes.
 *
 * Unverified pathways — deliberately unchanged, do not assume they were
 * checked (MOR-813):
 * - An MVL converting to a CVL on discovering insolvency (s496 / s513B
 *   interaction) still returns the appointment date. That conversion is
 *   exactly when voidable analysis starts to matter, so verify before relying
 *   on the window near its start.
 * - Provisional liquidation preceding a winding-up order likely follows the
 *   eventual order (no separate anchor from the provisional appointment);
 *   current assumed behaviour retained.
 * - A small business restructuring (Part 5.3B) that fails into liquidation
 *   still returns the appointment date, flagged `assumed`.
 *
 * Since MOR-901 this is the LAST tier of {@link deriveRelationBack}: it runs
 * only when there is no override and neither the recorded pathway nor the
 * dates could resolve the applicable s 91 rule.
 */
function deriveRelationBackDefault(job: Doc<"jobs">): RelationBackDefault {
  const type = job.appointmentType;
  // Normalise each candidate source date: a valid ISO string, or null.
  const iso = (raw: string | undefined): string | null => {
    const parsed = parseIsoDate(raw);
    return parsed ? formatIsoDate(parsed) : null;
  };
  const normalisedAppt = iso(job.appointmentDate);
  const windingUpApp = iso(job.windingUpApplicationDate);
  const adminStart = iso(job.administrationStartDate);

  switch (type) {
    case "cvl":
    case "simplified_liquidation": {
      // s446A route: the liquidation followed a voluntary administration, so
      // the s91 table relates back to the s513C day — the day the
      // administration began — not the day of the creditors' resolution.
      if (adminStart) {
        return {
          date: adminStart,
          applies: true,
          pending: false,
          assumed: false,
          reason: null,
          title: "Relation-back date (s91 — s513C day)",
          description:
            "Day the voluntary administration preceding this liquidation began (the s513C day) — the relation-back day where the liquidation followed an administration (s446A route).",
        };
      }
      return {
        date: normalisedAppt,
        applies: true,
        pending: normalisedAppt === null,
        assumed: false,
        // The missing-administration case is a silent under-reach: windows
        // start too late and drop the oldest slice with nothing in the UI
        // hinting at it. The advisory reaches the practitioner (Key Dates
        // panel) and the head agents (run context) — but absence of the date
        // does NOT mean an administration happened, so `assumed` stays false.
        reason: normalisedAppt
          ? "If this liquidation followed a voluntary administration, enter the administration start date: the relation-back day is then the day the administration began (the s513C day), not the resolution date."
          : "Enter the appointment date to set the relation-back day.",
        title: "Relation-back date (s91)",
        description:
          "Date of the winding-up resolution — the starting point for voidable transaction lookback windows. Where a voluntary administration preceded the liquidation, the relation-back day is instead the day the administration began (the s513C day).",
      };
    }

    case "mvl":
      // A members' voluntary winding up rarely meets voidable analysis; the
      // MVL→CVL conversion pathway (s496) is unverified — see the JSDoc.
      return {
        date: normalisedAppt,
        applies: true,
        pending: normalisedAppt === null,
        assumed: false,
        reason: normalisedAppt
          ? null
          : "Enter the appointment date to set the relation-back day.",
        title: "Relation-back date (s91)",
        description:
          "Date of the winding-up resolution — the starting point for voidable transaction lookback windows.",
      };

    case "court_liquidation": {
      // Order made while the company was under administration (or a DOCA that
      // has since failed): the s91 table relates back to the s513C day of the
      // original administration, not the filing date.
      if (adminStart) {
        return {
          date: adminStart,
          applies: true,
          pending: false,
          assumed: false,
          reason: null,
          title: "Relation-back date (s91 — s513C day)",
          description:
            "Day the voluntary administration began (the s513C day) — the relation-back day where the winding-up order was made while the company was under administration or a deed of company arrangement that has since failed. A failed deed traces back to the ORIGINAL administration.",
        };
      }
      return {
        date: windingUpApp,
        applies: true,
        pending: windingUpApp === null,
        assumed: false,
        reason: windingUpApp
          ? null
          : "Enter the date the winding-up application was filed (per ASIC notice) to set the relation-back day.",
        title: "Relation-back date (s91 — Court liquidation)",
        description:
          "Date the application for winding up was filed — the relation-back day for a court-ordered liquidation (s91). Where the order was made while the company was under administration or a since-failed deed, the relation-back day is instead the day that administration began (the s513C day).",
      };
    }

    case "provisional_liquidation": {
      // A provisional liquidation is not yet a winding up, but a winding-up
      // application is usually already on foot. Apply its filing date (falling
      // back to the provisional appointment date) so investigations can run
      // against the day s91 will fix on a winding up (MOR-636). Disclosed to
      // the practitioner via `assumed`.
      const provAssumed = windingUpApp ?? normalisedAppt;
      return {
        date: provAssumed,
        applies: true,
        pending: provAssumed === null,
        assumed: true,
        reason: provAssumed
          ? `Applied relation-back date is the ${windingUpApp ? "date the winding-up application was filed" : "date of the provisional appointment"} (${provAssumed}), so investigations can be conducted. This relation-back date applies if the company goes into liquidation. Only a liquidator has the power to recover transactions.`
          : "Enter the winding-up application date (or provisional appointment date) to set the applied relation-back date.",
        title: "Applied relation-back date (provisional liquidation)",
        description:
          "Applied relation-back date for a provisional liquidation: the winding-up application date, or the provisional appointment date. Only a liquidator has the power to recover transactions.",
      };
    }

    case "voluntary_administration": {
      // A VA is not a winding up, so there is no statutory relation-back day.
      // Practitioners still need a date to run investigations; we apply the
      // date of appointment (MOR-636). Disclosed via `assumed`.
      return {
        date: normalisedAppt,
        applies: true,
        pending: normalisedAppt === null,
        assumed: true,
        reason: normalisedAppt
          ? `Applied relation-back date is the date of appointment (${normalisedAppt}), so investigations can be conducted. This relation-back date applies if the company goes into liquidation. Only a liquidator has the power to recover transactions.`
          : "Enter the appointment date to set the applied relation-back date.",
        title: "Applied relation-back date",
        description:
          "Applied relation-back date is the date of appointment, so investigations can be conducted. Only a liquidator has the power to recover transactions.",
      };
    }

    case "deed_of_company_arrangement":
    case "sbr": {
      // DOCA / SBR are not windings up, so the relation-back day is applied
      // rather than statutory for now: the date of appointment, so the
      // recoveries a liquidator could pursue can be investigated for the
      // report to creditors (MOR-636). Disclosed via `assumed`.
      return {
        date: normalisedAppt,
        applies: true,
        pending: normalisedAppt === null,
        assumed: true,
        reason: normalisedAppt
          ? `Applied relation-back date is the date of appointment (${normalisedAppt}), so investigations can be conducted. This relation-back date applies if the company goes into liquidation. Only a liquidator has the power to recover transactions.`
          : "Enter the appointment date to set the applied relation-back date.",
        title: "Applied relation-back date",
        description:
          "Applied relation-back date is the date of appointment, so investigations can be conducted. Only a liquidator has the power to recover transactions.",
      };
    }

    case "receivership":
      return {
        date: null,
        applies: false,
        pending: false,
        assumed: false,
        reason:
          "Receivership is not a winding up — no relation-back day applies (the receiver acts for the secured creditor and does not pursue s588FE voidables).",
        title: "Relation-back date",
        description: "Not applicable — receivership is not a winding up.",
      };

    case "scheme_of_arrangement":
      return {
        date: null,
        applies: false,
        pending: false,
        assumed: false,
        reason:
          "A scheme of arrangement is not a winding up — no relation-back day applies.",
        title: "Relation-back date",
        description:
          "Not applicable — a scheme of arrangement is not a winding up.",
      };

    case "personal_bankruptcy":
    case "business_bankruptcy":
    case "creditors_petition":
    case "debtors_petition":
      return bankruptcyRelationBackDefault(
        deriveBankruptcyCommencement(bankruptcyInputsFromJob(job)),
      );

    case "part_x_arrangement":
      return {
        date: null,
        applies: false,
        pending: false,
        assumed: false,
        reason:
          "A Part X personal insolvency agreement is not a bankruptcy — no relation-back day applies.",
        title: "Relation-back date",
        description:
          "Not applicable — a Part X personal insolvency agreement is not a bankruptcy.",
      };

    default:
      // Pre-appointment / unset appointment type.
      return {
        date: null,
        applies: false,
        pending: true,
        assumed: false,
        reason: "Appointment details not yet recorded.",
        title: "Relation-back date",
        description: "Not yet applicable.",
      };
  }
}

// ---------------------------------------------------------------------------
// Anchor derivations
// ---------------------------------------------------------------------------

/**
 * Anchor dates that practitioners reason about constantly: appointment,
 * commencement, relation-back, court order, act of bankruptcy. Every
 * downstream lodgement / time-limit calculation hangs off these.
 *
 * Commencement = appointment date for now (the spec's "earlier appointment
 * date if connected" requires walking the corporate history; out of scope
 * for v1).
 *
 * The relation-back day is type-aware — see {@link deriveRelationBack}. It is
 * only surfaced as an anchor when one actually applies (so receivership,
 * scheme of arrangement, and pre-liquidation VA/DOCA/SBR get none).
 */
export function deriveAnchors(job: Doc<"jobs">): DerivedKeyDate[] {
  const out: DerivedKeyDate[] = [];
  const apptDate = parseIsoDate(job.appointmentDate);
  const courtDate = parseIsoDate(job.courtOrderDate);
  const actDate = parseIsoDate(job.actOfBankruptcyDate);

  if (apptDate) {
    out.push(
      buildAnchor(job, {
        code: "anchor_appointment",
        title: "Appointment date",
        description:
          "Date the firm was formally appointed (per the instrument of appointment).",
        date: formatIsoDate(apptDate),
      }),
    );
    out.push(
      buildAnchor(job, {
        code: "anchor_commencement",
        title: "Commencement date",
        description:
          "Current appointment date. If linked to an earlier appointment, override this entry with the earlier date.",
        date: formatIsoDate(apptDate),
      }),
    );
  }

  // Relation-back day — type-aware, only when one applies (and its source
  // date has been captured).
  const relationBack = deriveRelationBack(job);
  if (relationBack.date) {
    out.push(
      buildAnchor(job, {
        code: "anchor_relation_back",
        title: relationBack.title,
        description: relationBack.description,
        date: relationBack.date,
      }),
    );
  }

  if (courtDate) {
    out.push(
      buildAnchor(job, {
        code: "anchor_court_order",
        title: "Court order date",
        description: "Date the court ordered the appointment.",
        date: formatIsoDate(courtDate),
      }),
    );
  }

  if (actDate) {
    out.push(
      buildAnchor(job, {
        code: "anchor_act_of_bankruptcy",
        title: "Act of bankruptcy date",
        description: "Drives the BKY relation-back day computation.",
        date: formatIsoDate(actDate),
      }),
    );
  }

  return out;
}

function buildAnchor(
  job: Doc<"jobs">,
  partial: {
    code: string;
    title: string;
    description: string;
    date: string;
  },
): DerivedKeyDate {
  return {
    id: `derived:${job._id}:${partial.code}`,
    jobId: job._id,
    orgId: job.orgId,
    source: "statutory_override",
    category: "appointment",
    code: partial.code,
    title: partial.title,
    description: partial.description,
    date: partial.date,
    allDay: true,
    flag: "must",
    status: "upcoming",
    isDerived: true,
  };
}

// ---------------------------------------------------------------------------
// Statutory lodgement derivations (starter set)
// ---------------------------------------------------------------------------

/**
 * Configuration for a single derived lodgement. Kept declarative so the
 * registry stays scannable; new lodgements should land here.
 */
interface LodgementRule {
  code: string;
  title: string;
  description: string;
  flag: KeyDateFlag;
  /**
   * Receives the parsed appointment date; must return the due date or
   * null when the rule doesn't apply to this appointment.
   */
  computeDue: (
    appointmentDate: Date,
    appointmentType: AppointmentType | undefined,
  ) => Date | null;
  /** Optional gate; defaults to "all appointment types". */
  appliesTo?: (type: AppointmentType | undefined) => boolean;
}

const LODGEMENT_RULES: ReadonlyArray<LodgementRule> = [
  {
    code: "form_505_notice",
    title: "Notice of appointment (Form 505)",
    description:
      "Lodge notice of appointment with ASIC / publish PNW notice. Statutory: 1 business day under the Corps Act.",
    flag: "must",
    computeDue: (appt) => addBusinessDays(appt, 1),
    appliesTo: isCorporateAppointment,
  },
  {
    code: "ato_notification",
    title: "ATO + authority notification",
    description:
      "Notify the ATO and other relevant authorities. Statutory: within 14 days; aim for 1 business day.",
    flag: "must",
    computeDue: (appt) => addBusinessDays(appt, 1),
  },
  {
    code: "initial_creditor_notification",
    title: "Initial notification to creditors",
    description:
      "First creditor notice (Form 5604 / Form 205 / PNW). Corporate / VA appointments — window varies by appointment type. Bankruptcy uses its own creditor-notification rule.",
    flag: "must",
    computeDue: (appt, type) => {
      const days = creditorNotificationBusinessDays(type);
      return days == null ? null : addBusinessDays(appt, days);
    },
    // Bankruptcy has a dedicated creditor-notification rule (AFSA framing).
    appliesTo: (type) => !isPersonalInsolvencyAppointmentType(type),
  },
  {
    code: "form_531_dirri",
    title: "DIRRI (Form 531) lodgement",
    description:
      "Declaration of independence, relevant relationships and indemnities. Defaults to initial-notification due date.",
    flag: "must",
    computeDue: (appt, type) => {
      const days = creditorNotificationBusinessDays(type);
      if (days == null) return null;
      // DIRRI lodgement is "initial notification or earlier", and is not
      // required for court liquidation or bankruptcy.
      if (
        type === "court_liquidation" ||
        isPersonalInsolvencyAppointmentType(type)
      ) {
        return null;
      }
      return addBusinessDays(appt, days);
    },
    appliesTo: (type) =>
      type !== undefined &&
      type !== "court_liquidation" &&
      !isPersonalInsolvencyAppointmentType(type),
  },
  {
    code: "rocap_receipt",
    title: "ROCAP receipt due",
    description:
      "ROCAP receipt window. Varies by appointment type — CVL 5BD, CL 10BD, VA 5BD. Not applicable to bankruptcy (see Statement of Affairs).",
    flag: "must",
    computeDue: (appt, type) => rocapReceiptDue(appt, type),
    // ROCAP is a Corps Act instrument; bankruptcy uses a Statement of Affairs.
    appliesTo: (type) => !isPersonalInsolvencyAppointmentType(type),
  },
  {
    code: "rocap_lodgement",
    title: "ROCAP lodgement (Form 507 Part A)",
    description:
      "Lodge Part A of the ROCAP with ASIC once received. Default = receipt due date + 10BD (CVL), 5BD (CL/VA). Override the receipt date to re-flow.",
    flag: "must",
    computeDue: (appt, type) => {
      const receipt = rocapReceiptDue(appt, type);
      if (!receipt) return null;
      const lodgeWindow = rocapLodgementBusinessDays(type);
      return lodgeWindow == null ? null : addBusinessDays(receipt, lodgeWindow);
    },
    appliesTo: (type) =>
      type !== undefined && !isPersonalInsolvencyAppointmentType(type),
  },
  {
    code: "raea_request",
    title: "Request Assistance of External Administration (RAEA)",
    description:
      "Submit RAEA via the Regulator Portal when books & records / ROCAP can't be obtained. Target: appointment + 1 month, BEFORE the statutory report.",
    flag: "event_driven",
    computeDue: (appt) => addCalendarMonths(appt, 1),
    appliesTo: (type) =>
      type === undefined ? false : !isPersonalInsolvencyAppointmentType(type),
  },
  {
    code: "form_5601_statutory_report",
    title: "Statutory Report by Liquidator (Form 5601)",
    description:
      "IPR 70-40 — due appointment + 3 months. CVL / liquidation appointments only.",
    flag: "must",
    computeDue: (appt) => addCalendarMonths(appt, 3),
    appliesTo: (type) =>
      type === "cvl" ||
      type === "mvl" ||
      type === "simplified_liquidation" ||
      type === "court_liquidation" ||
      type === "provisional_liquidation",
  },
  {
    code: "va_first_creditors_meeting",
    title: "First creditors meeting (s436E)",
    description:
      "Hold the first creditors meeting within 8 business days of appointment.",
    flag: "must",
    computeDue: (appt) => addBusinessDays(appt, 8),
    appliesTo: (type) => type === "voluntary_administration",
  },
  {
    code: "form_530_va_report",
    title: "Report to creditors (Form 530)",
    description:
      "VA s439A report. Convening period − 1 business day (typically 4–5 weeks after appointment).",
    flag: "must",
    // 25 business days is the conservative default for the convening
    // period; practitioners will override on a per-matter basis.
    computeDue: (appt) => addBusinessDays(appt, 24),
    appliesTo: (type) => type === "voluntary_administration",
  },
  {
    code: "va_convening_period_end",
    title: "VA convening period ends",
    description:
      "End of the s439A convening period (default 25 business days). Second creditors meeting must be held within 5 BD of this date.",
    flag: "must",
    computeDue: (appt) => addBusinessDays(appt, 25),
    appliesTo: (type) => type === "voluntary_administration",
  },
  {
    code: "va_second_creditors_meeting",
    title: "Second creditors meeting deadline",
    description:
      "s439A second meeting must be held within 5 business days of the end of the convening period — default appointment + 30 BD.",
    flag: "must",
    computeDue: (appt) => addBusinessDays(appt, 30),
    appliesTo: (type) => type === "voluntary_administration",
  },
  {
    code: "doca_execution_deadline",
    title: "DOCA execution deadline (s444B(2))",
    description:
      "Where creditors resolve to enter into a DOCA, it must be executed within 15 business days of the second creditors meeting — default appointment + 45 BD.",
    flag: "event_driven",
    computeDue: (appt) => addBusinessDays(appt, 45),
    appliesTo: (type) => type === "voluntary_administration",
  },
  {
    code: "initial_insolvency_report",
    title: "Initial Insolvency Report (former EX01)",
    description:
      "Submit via the Regulator Portal alongside the statutory / VA report, or by appointment + 6 months at the latest.",
    flag: "event_driven",
    computeDue: (appt) => addCalendarMonths(appt, 6),
    appliesTo: (type) =>
      type === undefined ? false : !isPersonalInsolvencyAppointmentType(type),
  },
  {
    code: "aaf_application",
    title: "Assetless Administration Fund application",
    description:
      "Submit AAF funding via the Regulator Portal when no ROCAP / books and records are available.",
    flag: "event_driven",
    computeDue: (appt) => addCalendarMonths(appt, 2),
    appliesTo: (type) =>
      type === "cvl" ||
      type === "mvl" ||
      type === "simplified_liquidation" ||
      type === "court_liquidation",
  },
  {
    code: "form_5602_annual_return",
    title: "Annual administration return (Form 5602)",
    description: "Due appointment + 3 months, then annually until completion.",
    flag: "recurring",
    computeDue: (appt) => addCalendarMonths(appt, 3),
    appliesTo: (type) =>
      type === undefined ? false : !isPersonalInsolvencyAppointmentType(type),
  },
  {
    code: "bas_q1",
    title: "BAS / STP — first quarter after appointment",
    description:
      "Next quarterly BAS / STP cycle after appointment. Cadence flips to monthly/quarterly per ATO. Override or duplicate as needed.",
    flag: "recurring",
    computeDue: (appt) => addCalendarMonths(appt, 3),
    appliesTo: (type) =>
      type === undefined ? false : !isPersonalInsolvencyAppointmentType(type),
  },
  // Receivership-specific
  {
    code: "receivership_s422_report",
    title: "Receiver report (s422)",
    description:
      "Receiver report on the company's affairs — due within 2 months of appointment.",
    flag: "must",
    computeDue: (appt) => addCalendarMonths(appt, 2),
    appliesTo: (type) => type === "receivership",
  },
  // Simplified Liquidation
  {
    code: "simplified_eligibility_declaration",
    title: "Simplified Liquidation eligibility declaration",
    description:
      "Liquidator's declaration that the company meets the simplified-liquidation criteria — within 20 business days of appointment.",
    flag: "must",
    computeDue: (appt) => addBusinessDays(appt, 20),
    appliesTo: (type) => type === "simplified_liquidation",
  },
  // Bankruptcy-specific
  {
    code: "bky_statement_of_affairs",
    title: "Statement of Affairs due",
    description:
      "Bankrupt must file the Statement of Affairs with AFSA within 14 days of being notified of the sequestration order. Anchor is the date the bankrupt is notified — defaults from the appointment date; override the due date once the notification date is known.",
    flag: "must",
    computeDue: (appt) => addCalendarDays(appt, 14),
    // A debtor's petition is presented WITH the Statement of Affairs, so there
    // is no post-appointment filing deadline to track (MOR-1838).
    appliesTo: (type) =>
      isBankruptcyAppointment(type) && type !== "debtors_petition",
  },
  {
    code: "bky_creditor_notification",
    title: "Initial notification to creditors",
    description:
      "Trustee's first notice to known creditors advising of the bankruptcy and the trustee's appointment. Default: appointment + 10 business days.",
    flag: "must",
    computeDue: (appt, type) => {
      const days = creditorNotificationBusinessDays(type);
      return days == null ? null : addBusinessDays(appt, days);
    },
    appliesTo: isBankruptcyAppointment,
  },
  {
    code: "bky_initial_report",
    title: "Initial report to creditors",
    description:
      "Trustee's initial report to creditors on the administration of the estate. No fixed statutory date — default estimate appointment + 3 months; override per matter.",
    flag: "event_driven",
    computeDue: (appt) => addCalendarMonths(appt, 3),
    appliesTo: isBankruptcyAppointment,
  },
  {
    code: "bky_discharge",
    title: "Discharge from bankruptcy",
    description:
      "Automatic discharge under s149 — 3 years and 1 day from the filing of the statement of affairs. Default: appointment + 3 years (override once SoA is filed).",
    flag: "must",
    computeDue: (appt) => addCalendarYears(appt, 3),
    appliesTo: isBankruptcyAppointment,
  },
];

/**
 * Business-day window for the initial creditor notification, keyed by
 * appointment type. Returns null when the rule doesn't apply (e.g. types
 * we don't yet model precisely; the lodgement just won't appear).
 */
function creditorNotificationBusinessDays(
  type: AppointmentType | undefined,
): number | null {
  if (!type) return 10;
  switch (type) {
    case "cvl":
    case "mvl":
    case "simplified_liquidation":
      return 10;
    case "court_liquidation":
    case "provisional_liquidation":
      return 20;
    case "voluntary_administration":
    case "sbr":
      return 2;
    case "personal_bankruptcy":
    case "business_bankruptcy":
    case "creditors_petition":
    case "debtors_petition":
      return 10;
    case "deed_of_company_arrangement":
    case "receivership":
    case "scheme_of_arrangement":
      return 10;
    default:
      return 10;
  }
}

/** ROCAP receipt window per appointment type. Returns null when N/A. */
function rocapReceiptDue(
  appt: Date,
  type: AppointmentType | undefined,
): Date | null {
  if (!type) return addBusinessDays(appt, 5);
  switch (type) {
    case "cvl":
    case "mvl":
    case "simplified_liquidation":
      return addBusinessDays(appt, 5);
    case "court_liquidation":
    case "provisional_liquidation":
      return addBusinessDays(appt, 10);
    case "voluntary_administration":
    case "sbr":
      return addBusinessDays(appt, 5);
    case "personal_bankruptcy":
    case "business_bankruptcy":
    case "creditors_petition":
    case "debtors_petition":
      return addCalendarDays(appt, 14);
    default:
      return addBusinessDays(appt, 5);
  }
}

/** ROCAP lodgement window post-receipt per appointment type. */
function rocapLodgementBusinessDays(
  type: AppointmentType | undefined,
): number | null {
  if (!type) return 10;
  switch (type) {
    case "cvl":
    case "mvl":
    case "simplified_liquidation":
      return 10;
    case "court_liquidation":
    case "provisional_liquidation":
      return 5;
    case "voluntary_administration":
    case "sbr":
      return 5;
    case "personal_bankruptcy":
    case "business_bankruptcy":
    case "creditors_petition":
    case "debtors_petition":
      return null;
    default:
      return 10;
  }
}

/**
 * Compute the starter set of statutory lodgements for a job. Returns an
 * empty array when the job has no appointment date — pre-appointment
 * jobs don't have a clock to run against yet.
 */
export function deriveStatutoryLodgements(job: Doc<"jobs">): DerivedKeyDate[] {
  const appt = parseIsoDate(job.appointmentDate);
  if (!appt) return [];

  const out: DerivedKeyDate[] = [];
  for (const rule of LODGEMENT_RULES) {
    if (rule.appliesTo && !rule.appliesTo(job.appointmentType)) continue;
    const due = rule.computeDue(appt, job.appointmentType);
    if (!due) continue;
    out.push({
      id: `derived:${job._id}:${rule.code}`,
      jobId: job._id,
      orgId: job.orgId,
      source: "statutory_override",
      category: "lodgement",
      code: rule.code,
      title: rule.title,
      description: rule.description,
      date: formatIsoDate(due),
      allDay: true,
      flag: rule.flag,
      status: "upcoming",
      isDerived: true,
    });
  }
  return out;
}

/** Add N calendar years (preserves day-of-month). */
function addCalendarYears(date: Date, n: number): Date {
  return new Date(
    Date.UTC(date.getUTCFullYear() + n, date.getUTCMonth(), date.getUTCDate()),
  );
}

// ---------------------------------------------------------------------------
// Investigation / recovery review-window derivations
// ---------------------------------------------------------------------------

/**
 * Anchor + period for each "lookback" window the practitioner should
 * investigate. The DATE is the START of the window (i.e. how far back
 * to sweep transactions). Computed off relation-back date or
 * commencement date per the relevant Corps Act section.
 *
 * Skipped for bankruptcy appointments (different regime).
 */
interface InvestigationRule {
  code: string;
  title: string;
  description: string;
  /** Anchor to subtract the period from. */
  anchor: "relation_back" | "commencement";
  months: number;
  flag: KeyDateFlag;
}

const INVESTIGATION_RULES: ReadonlyArray<InvestigationRule> = [
  {
    code: "review_unfair_preference",
    title: "Unfair preference review window starts",
    description:
      "Sweep payments / credits to creditors from relation-back date − 6 months (capped to insolvency date if earlier).",
    anchor: "relation_back",
    months: 6,
    flag: "must",
  },
  {
    code: "review_uncommercial_transaction",
    title: "Uncommercial transaction review window starts",
    description: "Relation-back date − 2 years (s588FA / s588FB).",
    anchor: "relation_back",
    months: 24,
    flag: "must",
  },
  {
    code: "review_related_entity_insolvent_transaction",
    title: "Related entity insolvent transaction review starts",
    description:
      "Relation-back date − 2 years for related-entity preferences / uncommercials (longer of the two windows).",
    anchor: "relation_back",
    months: 24,
    flag: "must",
  },
  {
    code: "review_related_entity_preference",
    title: "Related entity preference / uncommercial review starts",
    description:
      "Relation-back date − 4 years for related-entity dealings (s588FC).",
    anchor: "relation_back",
    months: 48,
    flag: "must",
  },
  {
    code: "review_defeat_creditors",
    title: "Transactions to defeat creditors review starts",
    description:
      "Relation-back date − 10 years for fraudulent transactions (s588FE(5)).",
    anchor: "relation_back",
    months: 120,
    flag: "must",
  },
  {
    code: "review_newly_created_security",
    title: "Newly created circulating security review starts",
    description:
      "Relation-back date − 6 months for circulating security interests created in the lead-up to insolvency (s588FJ).",
    anchor: "relation_back",
    months: 6,
    flag: "must",
  },
  {
    code: "review_unreasonable_director_transaction",
    title: "Unreasonable director-related transaction review starts",
    description:
      "Relation-back date − 4 years (s588FDA). Does not require insolvency.",
    anchor: "relation_back",
    months: 48,
    flag: "must",
  },
  {
    code: "review_creditor_defeating_disposition",
    title: "Creditor-defeating dispositions review starts",
    description:
      "Relation-back date − 1 year for illegal phoenix activity (s588FDB).",
    anchor: "relation_back",
    months: 12,
    flag: "must",
  },
  {
    code: "review_ppsa_vesting",
    title: "PPSA vesting (registered out of time) review starts",
    description:
      "Commencement date − 6 months for security interests registered out of time (s588FL).",
    anchor: "commencement",
    months: 6,
    flag: "must",
  },
];

/**
 * Server shape for an investigation lookback row. Differs from a
 * `DerivedKeyDate` because the window has a START + END (not a single
 * point), and we want to surface the legislative section and anchor
 * label so the table can spell out the rule for the practitioner.
 *
 * Intentionally **not** rendered on the calendar — these are review
 * RANGES, not point-in-time events. The dedicated
 * `InvestigationWindowsPanel` renders the table view.
 */
/**
 * Anchor a review window measures from. The corporate windows use the
 * relation-back day / commencement; the Bankruptcy Act windows use the s 115
 * commencement calculator anchors (MOR-661).
 */
export type InvestigationWindowAnchorLabel =
  | "Relation-back date"
  | "Commencement date"
  | "Commencement of bankruptcy"
  | "Date of bankruptcy"
  | "Presentation of the petition";

export interface InvestigationWindow {
  code: string;
  title: string;
  description: string;
  section: string;
  /** Anchor the window measures back from. */
  anchorLabel: InvestigationWindowAnchorLabel;
  anchorDate: string;
  /** Human-readable period (e.g. "6 months"). */
  periodLabel: string;
  /**
   * ISO start of the lookback window (= anchor − period), or `null` for a head
   * with no statutory floor (s 121 transfers to defeat creditors).
   */
  reviewStartDate: string | null;
  /** ISO end of the lookback window (= anchor date). */
  reviewEndDate: string;
  /** Whether the window requires insolvency to be proven. */
  requiresInsolvency: boolean;
}

interface InvestigationRuleConfig extends InvestigationRule {
  section: string;
  requiresInsolvency: boolean;
  periodLabel: string;
}

const INVESTIGATION_TABLE_ROWS: ReadonlyArray<InvestigationRuleConfig> =
  INVESTIGATION_RULES.map((rule) => {
    // Per-rule metadata for the table (section number + insolvency
    // requirement) — kept close to the source rules so the table and
    // the legacy event derivation stay aligned.
    const meta: Record<
      string,
      { section: string; requiresInsolvency: boolean; periodLabel: string }
    > = {
      review_unfair_preference: {
        section: "s588FA",
        requiresInsolvency: true,
        periodLabel: "6 months",
      },
      review_uncommercial_transaction: {
        section: "s588FB",
        requiresInsolvency: true,
        periodLabel: "2 years",
      },
      review_related_entity_insolvent_transaction: {
        section: "s588FC",
        requiresInsolvency: true,
        periodLabel: "6 months / 2 years",
      },
      review_related_entity_preference: {
        section: "s588FC",
        requiresInsolvency: true,
        periodLabel: "4 years",
      },
      review_defeat_creditors: {
        section: "s588FE(5)",
        requiresInsolvency: true,
        periodLabel: "10 years",
      },
      review_newly_created_security: {
        section: "s588FJ",
        requiresInsolvency: true,
        periodLabel: "6 months",
      },
      review_unreasonable_director_transaction: {
        section: "s588FDA",
        requiresInsolvency: false,
        periodLabel: "4 years",
      },
      review_creditor_defeating_disposition: {
        section: "s588FDB",
        requiresInsolvency: false,
        periodLabel: "1 year",
      },
      review_ppsa_vesting: {
        section: "s588FL",
        requiresInsolvency: false,
        periodLabel: "6 months",
      },
    };
    return {
      ...rule,
      ...(meta[rule.code] ?? {
        section: "—",
        requiresInsolvency: false,
        periodLabel: `${rule.months} months`,
      }),
    };
  });

/**
 * Produce the investigation-window rows for the table panel.
 *
 * Returns one row per rule when a relation-back day applies. Voidable
 * transaction windows only exist in a winding up, so an empty array covers
 * every type without an RBD: receivership, scheme of arrangement,
 * pre-liquidation VA/DOCA/SBR/provisional, bankruptcy (its own regime), and
 * court liquidations whose winding-up application date hasn't been entered.
 *
 * `relation_back`-anchored rows measure back from the type-aware RBD
 * ({@link deriveRelationBack}); the lone `commencement`-anchored rule (s588FL)
 * still measures from the appointment date. (Court-liquidation s513A
 * commencement semantics are out of scope for MOR-542.)
 */
export function deriveInvestigationWindows(
  job: Doc<"jobs">,
): InvestigationWindow[] {
  // These are Corporations Act 2001 voidable-transaction windows — they never
  // apply to a bankruptcy (which has its own Bankruptcy Act regime), even
  // though a bankruptcy carries its own relation-back day. Nor to a Part X
  // arrangement (MOR-1838).
  if (isPersonalInsolvencyAppointmentType(job.appointmentType)) return [];
  const rbd = deriveRelationBack(job);
  if (!rbd.date) return [];
  const relationBack = parseIsoDate(rbd.date);
  if (!relationBack) return [];

  // Commencement defaults to the appointment date; fall back to the RBD when
  // no appointment date is recorded (e.g. court liq captured application-first).
  const commencement = parseIsoDate(job.appointmentDate) ?? relationBack;

  return INVESTIGATION_TABLE_ROWS.map((rule) => {
    const anchor =
      rule.anchor === "relation_back" ? relationBack : commencement;
    const windowStart = addCalendarMonths(anchor, -rule.months);
    return {
      code: rule.code,
      title: rule.title,
      description: rule.description,
      section: rule.section,
      anchorLabel:
        rule.anchor === "relation_back"
          ? "Relation-back date"
          : "Commencement date",
      anchorDate: formatIsoDate(anchor),
      periodLabel: rule.periodLabel,
      reviewStartDate: formatIsoDate(windowStart),
      reviewEndDate: formatIsoDate(anchor),
      requiresInsolvency: rule.requiresInsolvency,
    } satisfies InvestigationWindow;
  });
}

/**
 * Produce the Bankruptcy Act 1966 (Cth) review-window rows for a bankruptcy
 * job (MOR-661). These are the personal-regime analogue of
 * {@link deriveInvestigationWindows}: the three voidable-transfer heads that
 * anchor on the s 115 commencement calculator — never the corporate
 * relation-back machinery.
 *
 *   - s 120 undervalued transfers  commencement − 5 years → date of bankruptcy
 *   - s 121 transfers to defeat creditors  no floor → date of bankruptcy
 *   - s 122 preferences  presentation − 6 months (or commencement on a
 *     debtor's petition with a creditor's petition pending) → date of
 *     bankruptcy; the one head requiring insolvency to be proven.
 *
 * Mirrors the per-head window math in
 * `jobs/summarization/agents/flaggedTransactions/lib/windows.ts` so the review
 * panel and the flagged-transactions agent agree on every statutory period.
 * Returns an empty array for a non-bankruptcy job, or a bankruptcy job with no
 * date of bankruptcy recorded (the closing anchor for every window).
 */
export function deriveBankruptcyInvestigationWindows(
  job: Doc<"jobs">,
): InvestigationWindow[] {
  if (!isBankruptcyAppointment(job.appointmentType)) return [];
  const commencement = deriveBankruptcyCommencement(
    bankruptcyInputsFromJob(job),
  );
  const dob = commencement.dateOfBankruptcy;
  if (!dob) return [];

  const monthsBackIso = (isoDate: string, months: number): string | null => {
    const parsed = parseIsoDate(isoDate);
    return parsed ? formatIsoDate(addCalendarMonths(parsed, -months)) : null;
  };

  const out: InvestigationWindow[] = [];

  // s 120 — undervalued transfers: commencement − 5 years → date of bankruptcy.
  // Needs a s 115 commencement (not blocked, and not the legacy act-only path).
  if (
    commencement.commencement &&
    !commencement.blocked &&
    !commencement.legacy
  ) {
    const start = monthsBackIso(commencement.commencement, 60);
    if (start) {
      out.push({
        code: "review_bankruptcy_undervalued_transfer",
        title: "Undervalued transfers review window starts",
        description:
          "Transfers for less than market value in the 5 years before commencement of bankruptcy, up to the date of bankruptcy (s 120). Safe harbour (s 120(3)): a transfer more than 2 years (non-related) or 4 years (related entity) before commencement is protected where the transferee proves the bankrupt was solvent at the time.",
        section: "s 120",
        anchorLabel: "Commencement of bankruptcy",
        anchorDate: commencement.commencement,
        periodLabel: "5 years",
        reviewStartDate: start,
        reviewEndDate: dob,
        requiresInsolvency: false,
      });
    }
  }

  // s 121 — transfers to defeat creditors: no statutory floor, closing on the
  // date of bankruptcy. Always available once the date of bankruptcy is known.
  out.push({
    code: "review_bankruptcy_transfer_to_defeat_creditors",
    title: "Transfers to defeat creditors review window",
    description:
      "Every transfer of the bankrupt's property on or before the date of bankruptcy where the main purpose was to prevent, hinder or delay creditors (s 121). No look-back floor; the required purpose may be inferred where the bankrupt was, or became, insolvent at the time (s 121(2)).",
    section: "s 121",
    anchorLabel: "Date of bankruptcy",
    anchorDate: dob,
    periodLabel: "No floor",
    reviewStartDate: null,
    reviewEndDate: dob,
    requiresInsolvency: false,
  });

  // s 122 — preferences: 6 months before presentation of the petition (or from
  // commencement on a debtor's petition with a creditor's petition pending) →
  // date of bankruptcy. Insolvency at the time of each transfer is a positive
  // element, so this is the one bankruptcy head requiring insolvency.
  const fromCommencement =
    commencement.pathway === "s115_2_creditors_petition_pending" &&
    !!commencement.commencement;
  const preferenceStart = fromCommencement
    ? commencement.commencement
    : commencement.petitionPresentedDate
      ? monthsBackIso(commencement.petitionPresentedDate, 6)
      : null;
  if (preferenceStart) {
    out.push({
      code: "review_bankruptcy_preference",
      title: "Preferences review window starts",
      description: fromCommencement
        ? "A debtor's petition presented while a creditor's petition was pending runs from commencement of bankruptcy to the date of bankruptcy (s 122(1)(b)). The trustee must prove the bankrupt was insolvent at the time of each transfer."
        : "Payments or transfers to a creditor in the 6 months before presentation of the petition, up to the date of bankruptcy (s 122). The trustee must prove the bankrupt was insolvent at the time of each transfer.",
      section: "s 122",
      anchorLabel: fromCommencement
        ? "Commencement of bankruptcy"
        : "Presentation of the petition",
      anchorDate: fromCommencement
        ? (commencement.commencement ?? preferenceStart)
        : (commencement.petitionPresentedDate ?? preferenceStart),
      periodLabel: fromCommencement ? "From commencement" : "6 months",
      reviewStartDate: preferenceStart,
      reviewEndDate: dob,
      requiresInsolvency: true,
    });
  }

  return out;
}

// ---------------------------------------------------------------------------
// Status + merge helpers (consumed by queries.ts)
// ---------------------------------------------------------------------------

/**
 * Compute the "today vs date" status for a key date. Persisted rows in
 * terminal states (`completed`, `deferred`, `not_applicable`) keep their
 * stored value; everything else flips to `overdue` when the date has
 * passed.
 */
export function computeRuntimeStatus(
  date: string,
  storedStatus: KeyDateStatus,
  now: Date,
): KeyDateStatus {
  if (
    storedStatus === "completed" ||
    storedStatus === "deferred" ||
    storedStatus === "not_applicable"
  ) {
    return storedStatus;
  }
  const parsed = parseIsoDate(date);
  if (!parsed) return storedStatus;
  // Compare against UTC midnight of today.
  const today = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );
  return parsed.getTime() < today.getTime() ? "overdue" : "upcoming";
}

/**
 * All derived events that belong on the **calendar** — anchors,
 * statutory lodgements, and action deadlines. Investigation lookback
 * windows are NOT included; they are date ranges, not points, and are
 * surfaced separately via {@link deriveInvestigationWindows} +
 * `InvestigationWindowsPanel`.
 */
export function deriveAll(job: Doc<"jobs">): DerivedKeyDate[] {
  return [...deriveAnchors(job), ...deriveStatutoryLodgements(job)];
}
